import { useMemo, useState, forwardRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import ReactModal from 'react-modal';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { userActions } from '_actions';
import TextInput from 'Docs/Components/TextInput';
import { ReactComponent as Calendar } from 'assets/icons/calendar-icon.svg';
import deleteIcon from '../assets/icons/delete-btn.svg';
import '../assets/styles/react-datepicker-modal.css';
import { dateTimeConstants } from '_constants';
import PropTypes from "prop-types";

const customStylesQualModals = {
  content: {
    maxWidth: 460,
    height: 'auto',
  },
};

const customStylesUpdateQualModal = {
  content: {
    width: 'auto',
    height: 'auto',
  },
};

export default function ManageQualifications({
  profileData,
  currentUserPermissions,
  refetchUser,
  qualificationsList,
}) {
  const dispatch = useDispatch();

  const [userQualsChecked, setUserQualsChecked] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedQualId, setSelectedQualId] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedOptionQualName, setSelectedOptionQualName] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTableFieldEdited, setIsTableFieldEdited] = useState(false);
  const [tableData, setTableData] = useState(null);
  const [initialQualsChecked, setInitialUserQualsChecked] = useState(null);

  const DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';
  const red = '#FF0000';
  const grey = '#CCCCCC';

  useEffect(() => {
    if (profileData.qualificationdetails) {
      setTableData(profileData.qualificationdetails);

      const checkboxesChecked = profileData.qualificationdetails
        .filter((qualification) => qualification?.quals_checked === 1)
        .map((qualification) => qualification?.qualification_id);

      setUserQualsChecked(checkboxesChecked);
      setInitialUserQualsChecked(checkboxesChecked);
    }
  }, [profileData]);

  const handleIssuingAuthority = (index, event) => {
    //handle asynchronous issuing authority change functionality in the table
    setIsTableFieldEdited(true);
    const tempTableData = tableData?.map((value, tableIndex) => {
      if (tableIndex === index) {
        return {
          ...value,
          issuing_authority: event.target.value,
        };
      }
      return value;
    });
    setTableData(tempTableData);
  };

  const handleOnSubmit = async (values) => {
    //handle Add qualifications functionality
    setIsSubmitting(true);
    let data = new FormData();

    if (selectedOptionQualName?.__isNew__) {
      //appending data for a new qualification
      data.append('quals_short_form', values?.quals_short_form);
      data.append('is_new_qual', 1);
      data.append('quals_name', selectedOptionQualName?.label);
      data.append('color_code', values?.color_code);
    } else {
      data.append('quals_id', values?.quals_id);
    }

    data.append(
      'tsk_book_ini_date',
      values?.tsk_book_ini_date
        ? moment(values?.tsk_book_ini_date)?.format(DATE_FORMAT)
        : '',
    );

    data.append(
      'qualifying_date',
      values?.quals_date ? moment(values.quals_date)?.format(DATE_FORMAT) : '',
    );

    data.append(
      'expiration_date',
      values?.exp_date ? moment(values?.exp_date)?.format(DATE_FORMAT) : '',
    );

    data.append('issuing_authority', values?.issuing_authority);
    data.append('user_id', profileData?.id);

    let res;
    try {
      res = await dispatch(userActions.addQualification(data));
      if (res.success) {
        refetchUser();
        setIsTableFieldEdited(false);
        closeSubmitModal();
        return toast.success(res?.message);
      } else if (!res.success) {
        return toast.error(res?.message);
      }
    } catch (error) {
      let errorMessage = 'Something went wrong!';
      setIsSubmitting(false);
      const res = JSON.parse(error);
      if (res?.validation_error == false) {
        errorMessage = res?.message;
      } else {
        if (res.message) {
          Object.keys(res.message).some((key) => {
            if (res.message[key].length > 0) {
              errorMessage = res.message[key][0];
              return true;
            }
            return false;
          });
        }
      }
      return toast.error(errorMessage);
    }
  };

  const QualificationItemMobile = ({ data, index }) => (
    <div key={index}>
      <hr></hr>
      <div className='status_item'>
        <div className='list_item'>
          <strong>Qualification</strong>
        </div>
        <div className='mobile_qualification_item'>
          <input
            type='checkbox'
            id={'checkbox_current_qualification' + data?.qualification?.key}
            checked={userQualsChecked?.includes(data?.qualification_id)}
            name='currentQualification'
            onChange={(event) =>
              handleCheckboxChange(event, data?.qualification_id)
            }
            disabled={
              !currentUserPermissions?.includes('Manage Qualifications')
            }
          />
          <label
            htmlFor={
              'checkbox_current_qualification' + data?.qualification?.key
            }
          >
            <span />
          </label>
          <span className='wpcf7-list-item-label'>
            {data?.qualification?.key}
          </span>
          {currentUserPermissions?.includes('Manage Qualifications') && (
            <span
              style={{ cursor: 'pointer', marginLeft: '10px' }}
              onClick={() => {
                openDeleteModal(data?.id);
              }}
            >
              <img
                src={deleteIcon}
                alt='delete document'
                style={{ width: '15px' }}
              />
            </span>
          )}
        </div>
      </div>
      <div className='status_item'>
        <div className='list_item'>
          <strong>Tsk Book Initiated</strong>
        </div>
        <div className='mobile_qualification_item'>
          <DatePicker
            dateFormat={dateTimeConstants.DATE_FORMAT}
            maxDate={moment()} //max date shall not  be more than the current date
            name='tsk_book_ini_date'
            selected={
              data?.tsk_book_ini_date ? moment(data?.tsk_book_ini_date) : ''
            }
            onChange={(date) => {
              // Check if the selected date is greater than the current date
              if (date && moment(date).isAfter(moment())) {
                handleDateChange(index, null, 'tsk'); // Clear the input by setting it to null
              } else {
                handleDateChange(index, date, 'tsk'); // Update the date if it's valid
              }
            }}
            showMonthDropdown
            showYearDropdown
            dropdownMode='select'
            disabled={
              !currentUserPermissions?.includes('Manage Qualifications')
            }
            popperClassName='datePickerTop0'
          />
        </div>
      </div>
      <div className='status_item'>
        <div className='list_item'>
          <strong>Qualifying Date</strong>
        </div>
        <div className='mobile_qualification_item'>
          <DatePicker
            dateFormat={dateTimeConstants.DATE_FORMAT}
            maxDate={moment()} //max date shall not  be more than the current date
            name='qualifying_date'
            selected={
              data?.qualifying_date ? moment(data?.qualifying_date) : ''
            }
            onChange={(date) => {
              // Check if the selected date is greater than the current date
              if (date && moment(date).isAfter(moment())) {
                handleDateChange(index, null, 'qual'); // Clear the input by setting it to null
              } else {
                handleDateChange(index, date, 'qual'); // Update the date if it's valid
              }
            }}
            showMonthDropdown
            showYearDropdown
            dropdownMode='select'
            disabled={
              !currentUserPermissions?.includes('Manage Qualifications')
            }
            popperClassName='datePickerTop0'
          />
        </div>
      </div>
      <div className='status_item'>
        <div className='list_item'>
          <strong>Expiration Date</strong>
        </div>
        <div className='mobile_qualification_item'>
          <DatePicker
            dateFormat={dateTimeConstants.DATE_FORMAT}
            minDate={moment()} //minimum date shall not  be lesser than the current date
            name='expiration_date'
            selected={
              data?.expiration_date ? moment(data?.expiration_date) : ''
            }
            onChange={(date) => {
              // Check if the selected date is lesser than the current date
              if (date && moment(date).isBefore(moment())) {
                handleDateChange(index, null, 'exp'); // Clear the input by setting it to null
              } else {
                handleDateChange(index, date, 'exp'); // Update the date if it's valid
              }
            }}
            showMonthDropdown
            showYearDropdown
            dropdownMode='select'
            disabled={
              !currentUserPermissions?.includes('Manage Qualifications')
            }
            popperClassName='datePickerTop0'
          />
        </div>
      </div>
      <div className='status_item'>
        <div className='list_item'>
          <strong>Issuing Authority</strong>
        </div>
        <div className='mobile_qualification_item'>
          <div className='mobile_input_container'>
            <input
              type='text'
              name='issuing_authority'
              onChange={(event) => handleIssuingAuthority(index, event)}
              value={data?.issuing_authority ? data?.issuing_authority : ''}
              disabled={
                !currentUserPermissions?.includes('Manage Qualifications')
              }
            />
          </div>
        </div>
      </div>
    </div>
  );

  const handleUpdate = async () => {
    //handle Update quailfications details of the user functionality
    let data = new FormData();
    data.append('user_id', profileData?.id);
    const qualificationDetails = tableData?.map((item) => ({
      qualification_id: item?.qualification_id,
      qualifying_date: item?.qualifying_date || null,
      issuing_authority: item?.issuing_authority || null,
      tsk_book_ini_date: item?.tsk_book_ini_date || null,
      expiration_date: item?.expiration_date || null,
      id: item?.id || null,
      quals_checked: userQualsChecked?.includes(item?.qualification_id) ? 1 : 0,
    }));

    data.append('qualificationDetails', JSON.stringify(qualificationDetails));

    let res;
    try {
      res = await dispatch(userActions.updateQualification(data));
      if (res.success) {
        refetchUser();
        setIsTableFieldEdited(false);
        setIsUpdateModalOpen(false);
        setIsSubmitting(false);
        return toast.success(res?.message);
      } else if (!res.success) {
        return toast.error(res?.message);
      }
    } catch (error) {
      let errorMessage = 'Something went wrong!';
      setIsSubmitting(false);
      const res = JSON.parse(error);
      if (res?.validation_error == false) {
        errorMessage = res?.message;
      } else {
        if (res.message) {
          Object.keys(res.message).some((key) => {
            if (res.message[key].length > 0) {
              errorMessage = res.message[key][0];
              return true;
            }
            return false;
          });
        }
      }
      return toast.error(errorMessage);
    }
  };

  const closeSubmitModal = () => {
    setIsAddModalOpen(false);
    resetAddQual();
    setIsSubmitting(false);
  };

  const errorStyles = (field) => ({
    control: (baseStyles, state) => ({
      ...baseStyles,
      borderColor: field ? red : grey,
    }),
  });

  const openDeleteModal = (qualId) => {
    setSelectedQualId(qualId);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteSubmitModal = () => {
    setSelectedQualId(null);
    setIsDeleteModalOpen(false);
  };

  const handleDeleteQualSubmit = async (event) => {
    event.preventDefault();
    let data = new FormData();
    data.append('id', selectedQualId);

    try {
      const res = await dispatch(userActions.removeQualification(data));
      if (res?.success) {
        refetchUser();
        setSelectedQualId(null);
        setIsDeleteModalOpen(false);
        setTableData((prevData) =>
          prevData.filter((item) => item.id !== selectedQualId),
        );
        return toast.success(res?.message);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleDateChange = (index, date, flag) => {
    setIsTableFieldEdited(true);

    const formatTableData = (field) => {
      const updatedTableData = tableData?.map((obj, objIndex) => {
        if (objIndex === index) {
          return {
            ...obj,
            [field]: date ? moment(date).format(DATE_FORMAT) : '',
          };
        }
        return obj;
      });
      setTableData(updatedTableData);
    };

    switch (flag) {
      case 'tsk':
        formatTableData('tsk_book_ini_date');
        break;
      case 'qual':
        formatTableData('qualifying_date');
        break;
      case 'exp':
        formatTableData('expiration_date');
        break;
      default:
        break;
    }
  };

  const handleCheckboxChange = (event, checkboxId) => {
    setIsTableFieldEdited(true);
    const isChecked = event?.target?.checked;

    if (isChecked) {
      // Checkbox is checked, add the ID to the array
      setUserQualsChecked((prevChecked) => [...prevChecked, checkboxId]);
    } else {
      // Checkbox is unchecked, remove the ID from the array
      setUserQualsChecked((prevChecked) =>
        prevChecked.filter((id) => id !== checkboxId),
      );
    }
  };

  const handleCancel = () => {
    //revert any changes made in the table before proceeding to save the changes
    setTableData((prev) => profileData?.qualificationdetails);
    setIsTableFieldEdited(false);
    setUserQualsChecked(initialQualsChecked);
    setIsUpdateModalOpen(false);
  };

  const qualWithoutDates = useMemo(() => {
    //list of qualifications to display in Add qualifications section
    let items = [];
    qualificationsList?.forEach((item) => {
      let qualDetails = profileData?.qualificationdetails.find(
        (qual) => qual.qualification_id === item?.id,
      );
      if (!qualDetails) {
        items?.push({
          label: item?.value,
          value: item?.id,
          key: item?.key,
        });
      }
    });
    return items;
  }, [qualificationsList, profileData]);

  const CustomDatePickerInput = forwardRef(
    ({ value, onClick, onChange }, ref) => (
      <div className='qualification-datepicker' onClick={onClick}>
        <input type='text' value={value} onChange={onChange} ref={ref} />
        <Calendar className='svg-icon' />
      </div>
    ),
  );

  // First form instance for Add Qualifications
  const formAddQual = useForm({
    defaultValues: {
      quals_id: '',
      tsk_book_ini_date: '',
      quals_date: '',
      exp_date: '',
      quals_short_form: '',
      issuing_authority: '',
      color_code: '',
    },
  });

  const {
    control: controlAddQual,
    handleSubmit: handleSubmitAddQual,
    formState: { errors: errorsAddQual },
    reset: resetAddQual,
  } = formAddQual;

  return (
    <div className='mb-2'>
      <div className='qualifications_header-tab'>
        <h3>Qualifications</h3>
        {currentUserPermissions?.includes('Manage Qualifications') && (
          <div className='qualifications_btn_grp'>
            <button
              style={{ display: isTableFieldEdited && 'none' }}
              className='button'
              onClick={() => setIsAddModalOpen(true)}
            >
              Add Qualification
            </button>
            {isTableFieldEdited && (
              <>
                <button className='button' onClick={handleCancel}>
                  Cancel
                </button>
                <button
                  className='button'
                  onClick={() => setIsUpdateModalOpen(true)}
                >
                  Save Changes
                </button>
              </>
            )}
          </div>
        )}
      </div>
      <table
        className='tab_hide qualification_table'
        style={{ width: '100%', marginBottom: '20px' }}
      >
        <thead>
          <tr className='qualification_table_row'>
            <th>Qualification</th>
            <th>Tsk Book Initiated</th>
            <th>Qualifying Date</th>
            <th>Expiration Date</th>
            <th>Issuing Authority</th>
            <th> </th>
          </tr>
        </thead>
        {/* Desktop List */}
        <tbody className='qualification_table_tbody'>
          {tableData?.map((item, index) => {
            return (
              <tr key={item.id}>
                <td>
                  <div className='current_qualification_item'>
                    <input
                      type='checkbox'
                      id={
                        'checkbox_current_qualification' +
                        item?.qualification?.key
                      }
                      checked={userQualsChecked?.includes(
                        item?.qualification_id,
                      )}
                      name='currentQualification'
                      onChange={(event) =>
                        handleCheckboxChange(event, item?.qualification_id)
                      }
                      disabled={
                        !currentUserPermissions?.includes(
                          'Manage Qualifications',
                        )
                      }
                    />
                    <label
                      htmlFor={
                        'checkbox_current_qualification' +
                        item?.qualification?.key
                      }
                    >
                      <span />
                    </label>
                    <div>
                      <span className='wpcf7-list-item-label'>
                        {item?.qualification?.key}
                      </span>
                    </div>
                  </div>
                </td>
                <td>
                  <DatePicker
                    dateFormat={dateTimeConstants.DATE_FORMAT}
                    maxDate={moment()} //max date shall not  be more than the current date
                    name='tsk_book_ini_date'
                    selected={
                      item?.tsk_book_ini_date
                        ? moment(item?.tsk_book_ini_date)
                        : null
                    }
                    onChange={(date) => {
                      // Check if the selected date is greater than the current date
                      if (date && moment(date).isAfter(moment())) {
                        handleDateChange(index, null, 'tsk'); // Clear the input by setting it to null
                      } else {
                        handleDateChange(index, date, 'tsk'); // Update the date if it's valid
                      }
                    }}
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode='select'
                    disabled={
                      !currentUserPermissions?.includes('Manage Qualifications')
                    }
                    isClearable
                    popperClassName='datePickerTop0'
                  />
                </td>
                <td>
                  <DatePicker
                    dateFormat={dateTimeConstants.DATE_FORMAT}
                    maxDate={moment()} //max date shall not  be more than the current date
                    name='qualifying_date'
                    selected={
                      item?.qualifying_date
                        ? moment(item?.qualifying_date)
                        : null
                    }
                    onChange={(date) => {
                      // Check if the selected date is greater than the current date
                      if (date && moment(date).isAfter(moment())) {
                        handleDateChange(index, null, 'qual'); // Clear the input by setting it to null
                      } else {
                        handleDateChange(index, date, 'qual'); // Update the date if it's valid
                      }
                    }}
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode='select'
                    disabled={
                      !currentUserPermissions?.includes('Manage Qualifications')
                    }
                    isClearable
                    popperClassName='datePickerTop0'
                  />
                </td>
                <td>
                  <DatePicker
                    dateFormat={dateTimeConstants.DATE_FORMAT}
                    minDate={moment()} //minimum date shall not  be lesser than the current date
                    name='expiration_date'
                    selected={
                      item?.expiration_date
                        ? moment(item?.expiration_date)
                        : null
                    }
                    onChange={(date) => {
                      // Check if the selected date is lesser than the current date
                      if (date && moment(date).isBefore(moment())) {
                        handleDateChange(index, null, 'exp'); // Clear the input by setting it to null
                      } else {
                        handleDateChange(index, date, 'exp'); // Update the date if it's valid
                      }
                    }}
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode='select'
                    disabled={
                      !currentUserPermissions?.includes('Manage Qualifications')
                    }
                    isClearable
                    popperClassName='datePickerTop0'
                  />
                </td>
                <td>
                  <input
                    type='text'
                    name='issuing_authority'
                    onChange={(event) => handleIssuingAuthority(index, event)}
                    value={
                      item?.issuing_authority ? item?.issuing_authority : ''
                    }
                    disabled={
                      !currentUserPermissions?.includes('Manage Qualifications')
                    }
                    isClearable
                  />
                </td>
                <td>
                  {currentUserPermissions?.includes(
                    'Manage Qualifications',
                  ) && (
                    <span
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        openDeleteModal(item?.id);
                      }}
                    >
                      <img
                        src={deleteIcon}
                        alt='delete document'
                        style={{ width: '15px' }}
                      />
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {/* Mobile List */}
      <div className='tab_show'>
        <div className='history_items'>
          {tableData?.map((item, index) => (
            <QualificationItemMobile
              data={item}
              index={index}
              key={index}
              checkboxesChecked={userQualsChecked}
              currentUserPermissions={currentUserPermissions}
            />
          ))}
        </div>
      </div>

      {/* Modal: Create Qual */}
      <ReactModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        overlayClassName='alertPopup'
        style={customStylesQualModals}
      >
        <div id='statusPopup' className='popupModal'>
          <div className='modal-head'>
            <h4 className='modal-title'>Add Qualification</h4>
          </div>
          <form onSubmit={handleSubmitAddQual(handleOnSubmit)}>
            <div className='modal-body' style={{ textAlign: 'left' }}>
              <label>Qualification</label>
              <Controller
                rules={{ required: 'Qualification is required' }}
                name='quals_id'
                control={controlAddQual}
                render={({ field: { onChange }, fieldState }) => (
                  <div>
                    <Select
                      isDisabled={isSubmitting}
                      options={qualWithoutDates}
                      isClearable
                      onChange={(newDivision) => {
                        setSelectedOptionQualName(newDivision);
                        onChange(newDivision?.value);
                      }}
                      placeholder={'Select Qualification'}
                      className='basic-multi-select'
                      classNamePrefix='select'
                      styles={errorStyles(errorsAddQual?.quals_id)}
                    />
                    {errorsAddQual?.quals_id && (
                      <div className='qual_error_container'>
                        {fieldState.error.message}
                      </div>
                    )}
                  </div>
                )}
              />
              {selectedOptionQualName != null && (
                <>
                  <div className='addqual_field_container'>
                    <label>Tsk Book initiated</label>
                    <Controller
                      name='tsk_book_ini_date'
                      control={controlAddQual}
                      render={({ field: { value, onChange } }) => {
                        const isDateExceeded =
                          value &&
                          moment(
                            value,
                            dateTimeConstants.DATE_FORMAT,
                            true,
                          ).isValid() &&
                          moment(value).isAfter(moment());

                        // Clear the input if the date exceeds the current date
                        if (isDateExceeded) {
                          onChange(null); // Clear the input by setting the value to null
                        }
                        return (
                          <DatePicker
                            dateFormat={dateTimeConstants.DATE_FORMAT}
                            maxDate={moment()} //max date shall not  be more than the current date
                            disabled={isSubmitting}
                            className='returnDateField'
                            name='tsk_book_ini_date'
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode='select'
                            selected={value}
                            onChange={onChange}
                            customInput={
                              <CustomDatePickerInput onChange={onChange} />
                            }
                            popperClassName='datePickerTop0'
                          />
                        );
                      }}
                    />
                  </div>
                  <div className='addqual_field_container'>
                    <label>Qualifying date</label>
                    <Controller
                      name='quals_date'
                      control={controlAddQual}
                      render={({ field: { value, onChange } }) => {
                        const isDateExceeded =
                          value &&
                          moment(
                            value,
                            dateTimeConstants.DATE_FORMAT,
                            true,
                          ).isValid() &&
                          moment(value).isAfter(moment());

                        // Clear the input if the date exceeds the current date
                        if (isDateExceeded) {
                          onChange(null); // Clear the input by setting the value to null
                        }
                        return (
                          <DatePicker
                            dateFormat={dateTimeConstants.DATE_FORMAT}
                            maxDate={moment()} //max date shall not  be more than the current date
                            disabled={isSubmitting}
                            className='returnDateField'
                            name='quals_date'
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode='select'
                            selected={value}
                            onChange={onChange}
                            customInput={<CustomDatePickerInput />}
                            popperClassName='datePickerTop0'
                          />
                        );
                      }}
                    />
                  </div>
                  <div className='addqual_field_container'>
                    <label>Expiration date</label>
                    <Controller
                      name='exp_date'
                      control={controlAddQual}
                      render={({ field: { value, onChange } }) => {
                        const isDateLess =
                          value &&
                          moment(
                            value,
                            dateTimeConstants.DATE_FORMAT,
                            true,
                          ).isValid() &&
                          moment(value).isBefore(moment());

                        // Clear the input if the date exceeds the current date
                        if (isDateLess) {
                          onChange(null); // Clear the input by setting the value to null
                        }
                        return (
                          <DatePicker
                            dateFormat={dateTimeConstants.DATE_FORMAT}
                            minDate={moment()} //minimum date shall not  be lesser than the current date
                            disabled={isSubmitting}
                            className='returnDateField'
                            name='exp_date'
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode='select'
                            selected={value}
                            onChange={onChange}
                            customInput={<CustomDatePickerInput />}
                            popperClassName='datePickerTop0'
                          />
                        );
                      }}
                    />
                  </div>
                  <div className='addqual_field_container'>
                    <label>Issuing Authority</label>
                    <Controller
                      name='issuing_authority'
                      control={controlAddQual}
                      render={({ field: { value, onChange } }) => (
                        <TextInput
                          disabled={isSubmitting}
                          onChange={onChange}
                          value={value}
                        />
                      )}
                    />
                  </div>
                </>
              )}
              <div className='qual_btn_list'>
                <button
                  disabled={isSubmitting}
                  className='button grey mr-1'
                  onClick={closeSubmitModal}
                  type='button'
                >
                  Cancel
                </button>
                <button
                  disabled={isSubmitting}
                  className='button'
                  type='submit'
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </ReactModal>

      {/* Modal: Delete Qual */}
      <ReactModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        overlayClassName='alertPopup'
        style={customStylesQualModals}
      >
        <div id='statusPopup' className='popupModal'>
          <div className='modal-head'>
            <h4 className='modal-title-qual'>Remove Qualification</h4>
          </div>
          <form onSubmit={handleDeleteQualSubmit}>
            <div className='modal-body-qual' style={{ textAlign: 'left' }}>
              <p>
                Are you sure you want to remove this qualification from the
                profile?
              </p>
              <div className='qual_btn_list'>
                <button
                  disabled={isSubmitting}
                  className='button grey mr-1'
                  onClick={closeDeleteSubmitModal}
                  type='button'
                >
                  Cancel
                </button>
                <button
                  disabled={isSubmitting}
                  className='button'
                  type='submit'
                  style={{ backgroundColor: '#DA120B' }}
                >
                  Remove
                </button>
              </div>
            </div>
          </form>
        </div>
      </ReactModal>

      {/* Handle Update Modal */}
      <ReactModal
        isOpen={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
        overlayClassName='alertPopup'
        style={customStylesUpdateQualModal}
      >
        <div id='statusPopup' className='popupModal'>
          <div className='modal-head'>
            <h4 className='modal-title-qual'>
              Are you sure you want to save the changes?
            </h4>
          </div>
          <div className='modal-body-qual'>
            <p>Saving the changes will update the data.</p>
            <div className='updateQual_btn_list'>
              <button
                disabled={isSubmitting}
                className='button grey mr-1'
                onClick={handleCancel}
                type='button'
              >
                Cancel
              </button>
              <button
                disabled={isSubmitting}
                className='button'
                onClick={() => handleUpdate()}
                type='submit'
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </ReactModal>
    </div>
  );
}

ManageQualifications.propTypes = {
  profileData: PropTypes.object.isRequired,
  currentUserPermissions: PropTypes.array,
  refetchUser: PropTypes.func,
  qualificationsList: PropTypes.array,
};
